import { useState, useCallback, useEffect } from 'react';

export interface BeeTask {
  id: string;
  beeName: string;
  confidence: number;
  status: 'completed' | 'failed' | 'running' | 'pending';
  timestamp: Date;
  summary: string;
  output?: string;
  relatedTaskId?: string;
  buildCredits?: number;
}

interface ProvenanceEntry {
  id: string;
  sessionId: string;
  actor: 'user' | 'ai' | 'system';
  action: string;
  timestamp: Date;
  details?: Record<string, unknown>;
  relatedTaskId?: string;
}

interface ProvenanceData {
  entries: ProvenanceEntry[];
  beeTasks: BeeTask[];
  sessionSummary: {
    totalBuildCredits: number;
    totalTasks: number;
    successRate: number;
    duration: number;
  };
}

export const useProvenance = (sessionId?: string) => {
  const [data, setData] = useState<ProvenanceData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProvenance = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      const mockData: ProvenanceData = {
        entries: [
          {
            id: 'prov_001',
            sessionId: id,
            actor: 'user',
            action: 'session_start',
            timestamp: new Date(Date.now() - 3600000),
            details: { mode: 'semi-autonomous' }
          },
          {
            id: 'prov_002',
            sessionId: id,
            actor: 'ai',
            action: 'code_analysis',
            timestamp: new Date(Date.now() - 3000000),
            details: { files: 12, issues: 3 },
            relatedTaskId: 'task_001'
          },
          {
            id: 'prov_003',
            sessionId: id,
            actor: 'system',
            action: 'webhook_received',
            timestamp: new Date(Date.now() - 1800000),
            details: { source: 'github', event: 'pr_created' }
          },
          {
            id: 'prov_004',
            sessionId: id,
            actor: 'ai',
            action: 'deployment',
            timestamp: new Date(Date.now() - 900000),
            details: { environment: 'staging', status: 'success' },
            relatedTaskId: 'task_002'
          }
        ],
        beeTasks: [
          {
            id: 'task_001',
            beeName: 'CodeAnalyzer-Bee',
            confidence: 92,
            status: 'completed',
            timestamp: new Date(Date.now() - 3000000),
            summary: 'Analyzed codebase and identified 3 optimization opportunities',
            output: 'Found performance bottlenecks in data processing pipeline. Recommended implementing caching layer and optimizing database queries.',
            relatedTaskId: 'prov_002'
          },
          {
            id: 'task_002',
            beeName: 'Deploy-Bee',
            confidence: 88,
            status: 'completed',
            timestamp: new Date(Date.now() - 900000),
            summary: 'Successfully deployed application to staging environment',
            output: 'Deployment completed successfully. Application is running at https://staging.example.com with all health checks passing.',
            relatedTaskId: 'prov_004'
          },
          {
            id: 'task_003',
            beeName: 'Test-Bee',
            confidence: 45,
            status: 'failed',
            timestamp: new Date(Date.now() - 600000),
            summary: 'Failed to execute comprehensive test suite',
            output: 'Test execution failed due to missing environment variables. Please configure TEST_DATABASE_URL and API_KEY.',
            relatedTaskId: 'prov_005'
          },
          {
            id: 'task_004',
            beeName: 'Monitor-Bee',
            confidence: 76,
            status: 'running',
            timestamp: new Date(Date.now() - 300000),
            summary: 'Monitoring application performance metrics',
            relatedTaskId: 'prov_006'
          },
          {
            id: 'task_005',
            beeName: 'Security-Bee',
            confidence: 95,
            status: 'pending',
            timestamp: new Date(Date.now() - 120000),
            summary: 'Queued for security vulnerability scan',
            relatedTaskId: 'prov_007'
          }
        ],
        sessionSummary: {
          totalBuildCredits: 15420,
          totalTasks: 5,
          successRate: 0.6,
          duration: 3600
        }
      };

      setData(mockData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch provenance data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const recallTask = useCallback((taskId: string): BeeTask | null => {
    if (!data) return null;
    return data.beeTasks.find(task => task.id === taskId) || null;
  }, [data]);

  const getTasksByStatus = useCallback((status: BeeTask['status']): BeeTask[] => {
    if (!data) return [];
    return data.beeTasks.filter(task => task.status === status);
  }, [data]);

  const getRecentTasks = useCallback((limit: number = 5): BeeTask[] => {
    if (!data) return [];
    return data.beeTasks
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit);
  }, [data]);

  useEffect(() => {
    if (sessionId) {
      fetchProvenance(sessionId);
    }
  }, [sessionId, fetchProvenance]);

  return {
    data,
    isLoading,
    error,
    fetchProvenance,
    recallTask,
    getTasksByStatus,
    getRecentTasks
  };
};
